import { RefObject, useCallback, useEffect, useMemo } from 'react';

import { useScrollIntoView } from '@mantine/hooks';

import { useFormyContext } from '../useFormyContext';

type ScrollParams = Parameters<typeof useScrollIntoView>[0];
type ScrollIntoViewParams = Parameters<ReturnType<typeof useScrollIntoView>['scrollIntoView']>[0];

/**
 * Enables scrolling within a form
 *
 * on mount, the scrollableRef is assigned to the formScrollableRef
 * on scroll, the targetRef is assigned to the provided scrollTarget
 * on scroll finish, the onScrollFinish callback is called
 */
const useFormScroll = (scrollParams?: ScrollParams) => {
  const { formScrollableRef } = useFormyContext();

  const handleOnScrollFinish = useCallback(() => {
    scrollParams?.onScrollFinish?.();
  }, [scrollParams]);

  const { scrollableRef, targetRef, scrollIntoView, ...scrollProvider } = useScrollIntoView<
    HTMLDivElement,
    HTMLDivElement
  >({
    ...(scrollParams ?? {}),
    onScrollFinish: handleOnScrollFinish,
  });

  /**
   * Assigns the scrollableRef to the provided scrollTarget
   */
  const assignScrollableToFormScrollableRef = useCallback(() => {
    if (formScrollableRef?.current) {
      // @ts-expect-error -- TODO: Update the scrollableRef type to be a ref object
      scrollableRef.current = formScrollableRef.current;
    }
  }, [formScrollableRef, scrollableRef]);

  /** Assigns the scrollableRef to the formScrollableRef and the targetRef to the provided scrollTarget */
  const handleAssignRefs = useCallback(
    (scrollTarget: HTMLElement) => {
      assignScrollableToFormScrollableRef();
      /**  Match the targetRef to the provided scrollTarget */
      // @ts-expect-error -- TODO: Update the scrollableRef type to be a ref object
      targetRef.current = scrollTarget as unknown as RefObject<HTMLDivElement | null>;
    },
    [assignScrollableToFormScrollableRef, targetRef]
  );

  /**
   * Assigns the node to the target ref and scrolls to it passing through the scrollIntoView args
   */
  const scrollToNode = useCallback(
    (node: HTMLElement, args: ScrollIntoViewParams = {}) => {
      handleAssignRefs(node);
      scrollIntoView(args);
    },
    [handleAssignRefs, scrollIntoView]
  );

  const _scrollProvider = useMemo(
    () => ({
      ...scrollProvider,
      scrollToNode,
    }),
    [scrollProvider, scrollToNode]
  );

  useEffect(() => {
    assignScrollableToFormScrollableRef();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run on mount
  }, []);

  return _scrollProvider;
};

export { useFormScroll };
